import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppShell } from "../../components/Sidebar";
import { api } from "../../lib/api";
import { Search, Filter, Users, BookOpen, School, RotateCcw, X, FileSpreadsheet, FileText } from "lucide-react";
import { API } from "../../lib/api";

const downloadExport = (kind, format, params = {}) => {
  const token = localStorage.getItem("gd_token");
  const qs = new URLSearchParams({ format, ...params });
  // Open in new tab so the browser triggers the download via Authorization header? Use fetch + blob.
  fetch(`${API}${kind}?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m?.[1] || `report.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    });
};

const PROGRESS_OPTS = [
  { v: "", l: "All progress" },
  { v: "0-25", l: "0% – 25%" },
  { v: "26-50", l: "26% – 50%" },
  { v: "51-75", l: "51% – 75%" },
  { v: "76-100", l: "76% – 100%" },
];
const MOD_OPTS = [
  { v: "", l: "Any" },
  { v: "0-5", l: "0 – 5" },
  { v: "6-10", l: "6 – 10" },
  { v: "11-15", l: "11 – 15" },
  { v: "16-20", l: "16 – 20" },
];
const ACTIVE_OPTS = [
  { v: "", l: "Any time" },
  { v: "today", l: "Today" },
  { v: "yesterday", l: "Yesterday" },
  { v: "week", l: "This week" },
];

const initials = (name) => name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
    <div className="flex items-start gap-4">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
        <Icon size={20} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{sub}</p>
      </div>
    </div>
  </div>
);

export default function TeacherDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [grade, setGrade] = useState("8");
  const [division, setDivision] = useState("A");
  const [filterOpen, setFilterOpen] = useState(false);
  const [progress, setProgress] = useState("");
  const [modulesCompleted, setModulesCompleted] = useState("");
  const [lastActive, setLastActive] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});

  const load = async () => {
    const params = new URLSearchParams({ grade, division, search, lang: i18n.resolvedLanguage || "en" });
    if (appliedFilters.progress) params.set("progress", appliedFilters.progress);
    if (appliedFilters.modulesCompleted) params.set("modules_completed", appliedFilters.modulesCompleted);
    if (appliedFilters.lastActive) params.set("last_active", appliedFilters.lastActive);
    const { data } = await api.get(`/teacher/dashboard?${params}`);
    setData(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [grade, division, search, appliedFilters, i18n.resolvedLanguage]);

  const apply = () => setAppliedFilters({ progress, modulesCompleted, lastActive });
  const reset = () => { setProgress(""); setModulesCompleted(""); setLastActive(""); setAppliedFilters({}); };

  const stats = data?.stats;

  return (
    <AppShell role="teacher">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
            {t("teacher.dashboard.welcome", { name: data?.teacher?.full_name || t("sidebar.titles.teacher") })} <span aria-hidden>👋</span>
          </h1>
          <p className="mt-1 text-slate-500">{t("teacher.dashboard.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => downloadExport("/teacher/export/class", "xlsx", { grade, division })}
            data-testid="export-class-xlsx"
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
          >
            <FileSpreadsheet size={14} /> {t("teacher.dashboard.export_excel")}
          </button>
          <button
            onClick={() => downloadExport("/teacher/export/class", "pdf", { grade, division })}
            data-testid="export-class-pdf"
            className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100"
          >
            <FileText size={14} /> {t("teacher.dashboard.export_pdf")}
          </button>
        </div>
      </div>

      <div className={`mt-8 grid gap-6 ${filterOpen ? "lg:grid-cols-[1fr_280px]" : "lg:grid-cols-1"}`}>
        <div className="space-y-6">
          {/* School / Grade / Division */}
          <div className="grid gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-card sm:grid-cols-3">
            <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                <School size={18} />
              </div>
              <div>
                <p className="text-xs text-slate-500">{t("teacher.dashboard.school")}</p>
                <p className="text-sm font-semibold text-slate-900">{data?.teacher?.school_name || "—"}</p>
              </div>
            </div>
            <label className="flex flex-col gap-1 rounded-xl bg-slate-50 px-4 py-2.5">
              <span className="text-xs text-slate-500">{t("teacher.dashboard.grade")}</span>
              <select data-testid="grade-select" value={grade} onChange={(e) => setGrade(e.target.value)} className="bg-transparent text-base font-semibold text-slate-900 outline-none">
                {["6","7","8","9","10"].map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 rounded-xl bg-slate-50 px-4 py-2.5">
              <span className="text-xs text-slate-500">{t("teacher.dashboard.division")}</span>
              <select data-testid="division-select" value={division} onChange={(e) => setDivision(e.target.value)} className="bg-transparent text-base font-semibold text-slate-900 outline-none">
                {["A","B","C","D"].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          </div>

          {/* Stats */}
          <div className="grid gap-5 sm:grid-cols-2">
            <StatCard icon={Users} label={t("teacher.dashboard.total_students")} value={stats?.total_students ?? "—"} sub={t("teacher.dashboard.in_grade", { label: `${grade}${division}` })} color="bg-violet-100 text-violet-600" />
            <StatCard icon={BookOpen} label={t("teacher.dashboard.avg_modules_completed")} value={stats ? `${stats.avg_modules_completed} / ${stats.total_modules}` : "—"} sub={t("teacher.dashboard.across_all_students")} color="bg-emerald-100 text-emerald-600" />
          </div>

          {/* Students Table */}
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
            <h2 className="text-lg font-bold text-slate-900">{t("teacher.dashboard.students_in_grade", { label: `${grade}${division}` })}</h2>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative max-w-sm flex-1">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="search-students"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("teacher.dashboard.search_students")}
                  className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              <button
                onClick={() => setFilterOpen((v) => !v)}
                data-testid="toggle-filters"
                className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50"
              >
                <Filter size={14} /> {t("teacher.dashboard.filters")}
              </button>
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="py-3 pr-4">{t("teacher.dashboard.table.roll_no")}</th>
                    <th className="py-3 pr-4">{t("teacher.dashboard.table.name")}</th>
                    <th className="py-3 pr-4">{t("teacher.dashboard.table.modules_completed")}</th>
                    <th className="py-3 pr-4">{t("teacher.dashboard.table.quiz_score")}</th>
                    <th className="py-3 pr-4">{t("teacher.dashboard.table.last_active")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.students || []).map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => navigate(`/teacher/students/${s.id}`)}
                      data-testid={`student-row-${s.roll_no}`}
                      className="cursor-pointer border-b border-slate-50 transition-colors hover:bg-slate-50"
                    >
                      <td className="py-4 pr-4 font-medium text-slate-700">{s.roll_no}</td>
                      <td className="py-4 pr-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
                            {initials(s.full_name)}
                          </div>
                          <span className="font-medium text-slate-900">{s.full_name}</span>
                        </div>
                      </td>
                      <td className="py-4 pr-4 text-slate-700">{s.modules_completed} / 20</td>
                      <td className="py-4 pr-4">
                        {s.avg_quiz_score != null ? (
                          <span data-testid={`student-quiz-score-${s.roll_no}`} className="rounded-md bg-indigo-50 px-2.5 py-1 text-sm font-semibold text-indigo-700">{s.avg_quiz_score}%</span>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-4 pr-4 text-slate-600">{s.last_active}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data && data.students.length === 0 && (
                <p className="py-12 text-center text-sm text-slate-500">{t("teacher.dashboard.no_match")}</p>
              )}
            </div>
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
          <aside className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card lg:sticky lg:top-6 lg:h-fit">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">{t("teacher.dashboard.filters")}</h3>
              <button onClick={() => setFilterOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <button onClick={reset} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
              <RotateCcw size={14} /> {t("teacher.dashboard.clear_all")}
            </button>

            <div className="mt-5 space-y-4">
              <FilterSelect label={t("teacher.dashboard.progress")} value={progress} onChange={setProgress} options={PROGRESS_OPTS} testid="filter-progress" />
              <FilterSelect label={t("teacher.dashboard.modules_completed")} value={modulesCompleted} onChange={setModulesCompleted} options={MOD_OPTS} testid="filter-modules" />
              <FilterSelect label={t("teacher.dashboard.last_active")} value={lastActive} onChange={setLastActive} options={ACTIVE_OPTS} testid="filter-last-active" />
            </div>

            <div className="mt-6 flex gap-3">
              <button onClick={reset} data-testid="filter-reset" className="flex-1 rounded-xl border border-indigo-200 bg-white py-2.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50">{t("teacher.dashboard.reset")}</button>
              <button onClick={apply} data-testid="filter-apply" className="flex-1 rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">{t("teacher.dashboard.apply")}</button>
            </div>
          </aside>
        )}
      </div>
    </AppShell>
  );
}

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </label>
);
